import { useEffect, useState, useCallback } from "react";
import { useAuthContextV2 } from "../../../../auth-state";
import { Redirect, useHistory } from "react-router-dom";
import { PortalTemplate } from "../../../template/portal/default-template";
import { TtzkPortalR } from "../route";
import { Notice } from "../../../../application/domain/ttzk-portal";
import apiClient from "../../../../application/apis/api-client";
import { reportTtzkPortalRelatedError } from "../../../../application/domain/ttzk-portal/util/error";
import { toast } from "react-toastify";
import { RBACHidden } from "../../../helpers/rbac/rbac";
import {
  DangerButton,
  Pagination,
  PrimaryButton,
  StatusTag,
  WarningDialog,
} from "@graffer-inc/console-design-kit";
import { Paging } from "../../../../application/domain/proc-management";
import { parseQueryOfLocation } from "../../../../utils/url-utils";

export const NoticeList = () => {
  const { auth } = useAuthContextV2();
  const orgId = auth.data?.orgId;
  const history = useHistory();
  const [deleteTarget, setDeleteTarget] = useState<Notice | undefined>();
  const [notices, setNotices] = useState<Notice[]>([]);
  const [paging, setPaging] = useState<Paging>({
    current: 1,
    last: 1,
  });
  const { page } = parseQueryOfLocation(location);
  const pageNum =
    Number.isInteger(Number(page)) && Number(page) > 0
      ? Number(page)
      : undefined;

  const handlePageClick = useCallback(
    ({ page }: { page: number }) => {
      history.push(TtzkPortalR.NoticeList.link({ page }));
    },
    [history],
  );

  const fetchNoticesByOrgId = useCallback(
    async (orgId: string) => {
      try {
        const { data } = await apiClient.ttzkPortal.listNoticesByOrgId(
          orgId,
          pageNum ?? 1,
        );
        setNotices(data.items);
        setPaging(data.paging);
      } catch (error) {
        reportTtzkPortalRelatedError(error);
        const message = error.response?.data?.msg ?? error.message;
        toast.error(
          `お知らせ情報一覧の取得に失敗しました。 organizationId: ${orgId}, error message: ${message}`,
        );
      }
    },
    [pageNum],
  );

  useEffect(() => {
    if (orgId) {
      fetchNoticesByOrgId(orgId);
    }
  }, [fetchNoticesByOrgId, orgId, pageNum]);

  const deleteNotice = async (noticeId: string) => {
    try {
      const { data } = await apiClient.ttzkPortal.deleteNotice(noticeId);
      if (data) {
        toast.success("お知らせ情報の削除に成功しました。");
        setDeleteTarget(undefined);
        if (orgId) {
          fetchNoticesByOrgId(orgId);
        }
      }
    } catch (error) {
      reportTtzkPortalRelatedError(error);
      const message = error.response?.data?.msg ?? error.message;
      toast.error(
        `お知らせ情報の削除に失敗しました。 organizationId: ${orgId}, error message: ${message}`,
      );
    }
  };

  const isPublishDatePassed = (publishDate: string) => {
    const now = new Date();
    const publishDateObj = new Date(publishDate);
    return publishDateObj < now;
  };

  if (!auth.hasPermissions("portal.notices.list")) {
    return <Redirect to={TtzkPortalR.Home.link({})} />;
  }

  return (
    <PortalTemplate
      breadcrumbs={[
        { to: TtzkPortalR.Home.link({}), title: "Graffer ポータル" },
        { to: TtzkPortalR.NoticeList.link({}), title: "お知らせ情報一覧 " },
      ]}
    >
      <div className={"content"}>
        <h1>お知らせ情報一覧</h1>
        <hr />
        <div>
          <RBACHidden requirePermissions="ttzk_portal.shortcuts.create">
            <PrimaryButton
              text="＋お知らせ情報の新規作成"
              onClick={() => {
                history.push(TtzkPortalR.NoticeCreate.link({}));
              }}
            />
          </RBACHidden>
        </div>
      </div>
      <br />
      <table className={"table is-fullwidth"}>
        <thead>
          <tr>
            <th>公開日時</th>
            <th>状態</th>
            <th aria-label="強調表示の有無"></th>
            <th>タイトル</th>
            <th>最終更新日時</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {notices.map((notice) => (
            <tr key={notice.id}>
              <td>{new Date(notice.publishDate).toLocaleString()}</td>
              <td>
                {notice.isPublished ? (
                  isPublishDatePassed(notice.publishDate) ? (
                    <StatusTag type={"SUCCESS"}>公開</StatusTag>
                  ) : (
                    <StatusTag type={"SECONDARY"}>公開予定</StatusTag>
                  )
                ) : (
                  <StatusTag type={"LIGHT"}>下書き</StatusTag>
                )}
              </td>
              <td>
                {notice.isHighlighted && (
                  <StatusTag type={"ACCENT"}>強調表示</StatusTag>
                )}
              </td>
              <td>
                {notice.title} <br />
                <small>(ID: {notice.id})</small>
              </td>
              <td>{new Date(notice.updatedAt).toLocaleString()}</td>
              <td>
                <div className="buttons">
                  <RBACHidden requirePermissions="portal.notices.update">
                    <PrimaryButton
                      text="編集する"
                      onClick={() => {
                        history.push(
                          TtzkPortalR.NoticeEdit.link({ noticeId: notice.id }),
                        );
                      }}
                    />
                  </RBACHidden>
                  <RBACHidden requirePermissions="portal.notices.delete">
                    <DangerButton
                      text="削除する"
                      onClick={() => {
                        setDeleteTarget(notice);
                      }}
                    />
                  </RBACHidden>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div>
        <Pagination
          onPageClick={(page) => {
            handlePageClick({ page: page });
          }}
          currentPage={paging.current}
          lastPage={paging.last}
        />
      </div>
      {deleteTarget && (
        <WarningDialog
          title={"お知らせ情報の削除"}
          onProceed={() => {
            deleteNotice(deleteTarget.id);
          }}
          proceedText={"削除する"}
          onCancel={() => setDeleteTarget(undefined)}
          cancelText={"削除しない"}
          contents={
            <div className="container">
              <p>
                {`「${deleteTarget.title}(ID: ${deleteTarget.id})」のお知らせ情報を削除しますか？`}
              </p>
            </div>
          }
        />
      )}
    </PortalTemplate>
  );
};
